<script setup lang="ts">
import type { TFACurtailmentInterval } from '@/types/cpm'
import { DateTime } from 'luxon'
import { computed } from 'vue'
import { Icon } from '@iconify/vue'
import { useTFACurtailmentIntervals } from '@/composables/cpm/cpmProgramManagement'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import type { ITFACurtailmentIntervalFilters } from '@/types/cpm/filters'

const props = defineProps<{
  filters: ITFACurtailmentIntervalFilters
  /** Site timezone (e.g. from selected site) for displaying start/end datetimes. */
  siteTimezone: string
}>()

const emit = defineEmits<{
  (e: 'update-filters', value: Partial<ITFACurtailmentIntervalFilters>): void
}>()

const { getTFACurtailmentIntervalsQuery, updateTFACurtailmentIntervalMutation, deleteTFACurtailmentIntervalMutation } =
  useTFACurtailmentIntervals()

// Clone into the computed so nested filter changes (page, date, ...) produce a new
// reference and trigger query refetches -- returning props.filters directly would not.
const { data: curtailmentIntervalsData, isLoading: curtailmentIntervalsLoading } = getTFACurtailmentIntervalsQuery(
  computed(() => ({ ...props.filters }))
)

// Computed properties for curtailment ratio
const totalIntervals = computed(() => {
  return curtailmentIntervalsData.value?.total_records ?? 0
})

const curtailedIntervals = computed(() => {
  return curtailmentIntervalsData.value?.data?.filter((interval) => interval.curtailed).length ?? 0
})

defineExpose({
  totalIntervals,
  curtailedIntervals,
  isLoading: curtailmentIntervalsLoading,
})

const updateCurtailedStatus = (row: TFACurtailmentInterval, curtailed: boolean) => {
  if (!row.id) return

  updateTFACurtailmentIntervalMutation.mutate({
    id: row.id,
    curtailed,
  })
}

const confirmDelete = (row: TFACurtailmentInterval) => {
  if (!row.id) return

  DeleteConfirmationModal(
    'Delete Interval',
    'Are you sure you want to delete this interval?',
    row.id,
    deleteTFACurtailmentIntervalMutation.mutateAsync
  )
}

// Update pagination
const updatePage = (page: number) => {
  emit('update-filters', { page })
}

const updatePageSize = (pageSize: number) => {
  emit('update-filters', { per_page: pageSize })
}

const formatDatetimeInSiteTz = (_row: TFACurtailmentInterval, _col: unknown, value: string) => {
  return DateTime.fromISO(value, { zone: 'utc' }).setZone(props.siteTimezone).toFormat('MMM d, yyyy, HH:mm ZZZZ')
}
</script>

<template>
  <div class="flex flex-col flex-grow-1">
    <el-table
      v-loading="curtailmentIntervalsLoading"
      :data="curtailmentIntervalsData?.data ?? []"
      stripe
      style="width: 100%"
      layout="fixed"
      height="100%"
    >
      <template #empty>
        <el-empty description="No intervals found. Select a customer and site or add new intervals." />
      </template>
      <el-table-column label="Start" prop="start_datetime_utc" :formatter="formatDatetimeInSiteTz" sortable />
      <el-table-column label="End" prop="end_datetime_utc" :formatter="formatDatetimeInSiteTz" sortable />
      <el-table-column label="Curtailed">
        <template #default="{ row }">
          <el-checkbox
            v-model="row.curtailed"
            @change="(checked) => updateCurtailedStatus(row as TFACurtailmentInterval, !!checked)"
          />
        </template>
      </el-table-column>
      <el-table-column width="100" fixed="right">
        <template #default="{ row }">
          <el-button title="Delete" link @click="confirmDelete(row as TFACurtailmentInterval)">
            <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="curtailmentIntervalsData && curtailmentIntervalsData.total_pages > 0"
      class="flex justify-end mt-[var(--spacing-spacing-md)]"
      :current-page="props.filters.page"
      :page-size="props.filters.per_page"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="curtailmentIntervalsData?.total_pages"
      :total="curtailmentIntervalsData?.total_records"
      :disabled="curtailmentIntervalsLoading"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
      @current-change="updatePage"
      @size-change="updatePageSize"
    />
  </div>
</template>
